import React, { useEffect, useMemo, useState } from "react";
import {
  PackageCheck,
  Truck,
  Clock,
  CheckCircle2,
  Search,
  ShieldCheck,
  MapPin,
  User,
  CalendarDays,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

const DeliveryOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const role = localStorage.getItem("role");

  useEffect(() => {
    const fetchOrders = async () => {
      if (role !== "deliveryperson") {
        setLoading(false);

        await Swal.fire({
          icon: "error",
          title: "Unauthorized",
          text: "You are not authorized to access the delivery dashboard.",
          confirmButtonColor: "#2563EB",
        });

        return;
      }

      try {
        const response = await api.get("/get_all_orders/");

        setOrders(
          Array.isArray(response.data?.orders)
            ? response.data.orders
            : []
        );
      } catch (error) {
        console.error("Failed to load orders:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Orders",
          text:
            error.response?.data?.message ||
            error.response?.data?.detail ||
            "Failed to load delivery orders. Please try again.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [role]);

  const markAsDelivered = async (order) => {
    if (order.delivered) return;

    const result = await Swal.fire({
      icon: "question",
      title: "Confirm Delivery",
      html: `
        <p style="color:#64748b">
          Are you sure you want to mark
          <strong>${order.product_name}</strong>
          as delivered?
        </p>
      `,
      showCancelButton: true,
      confirmButtonColor: "#2563EB",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Yes, Delivered",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setUpdatingOrderId(order.id);

    try {
      await api.get(`/confirm_order/${order.id}/`);

      setOrders((prevOrders) =>
        prevOrders.map((item) =>
          item.id === order.id
            ? {
                ...item,
                delivered: true,
              }
            : item
        )
      );

      Swal.fire({
        icon: "success",
        title: "Delivery Confirmed",
        text: "The order has been marked as delivered.",
        confirmButtonColor: "#2563EB",
      });
    } catch (error) {
      console.error("Failed to update order:", error);

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Failed to update the delivery status.",
        confirmButtonColor: "#2563EB",
      });
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString("en-KE");
  };

  const formatDate = (date) => {
    if (!date) return "";

    return new Date(date).toLocaleString("en-KE", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const deliveredCount = useMemo(() => {
    return orders.filter((order) => order.delivered).length;
  }, [orders]);

  const pendingCount = useMemo(() => {
    return orders.filter((order) => !order.delivered).length;
  }, [orders]);

  const totalOrderValue = useMemo(() => {
    return orders.reduce((total, order) => {
      const price = Number(order.price || 0);
      const quantity = Number(order.quantity || 1);

      return total + price * quantity;
    }, 0);
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    return orders.filter((order) => {
      const matchesSearch =
        !search ||
        order.product_name?.toLowerCase().includes(search) ||
        order.user_name?.toLowerCase().includes(search);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "pending" && !order.delivered) ||
        (statusFilter === "delivered" && order.delivered);

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, statusFilter]);

  if (role !== "deliveryperson" && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center bg-white border border-slate-200 rounded-3xl p-10 shadow-xl shadow-slate-900/5">
          <div className="w-16 h-16 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck size={28} />
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            Access restricted
          </h1>

          <p className="text-slate-500">
            This page is available only to authorized delivery personnel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
            <Truck size={15} />
            Delivery Operations
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Delivery Dashboard
              </h1>

              <p className="text-slate-300 text-lg leading-relaxed">
                Review customer orders, track pending deliveries, and confirm
                completed deliveries from one place.
              </p>
            </div>

            {!loading && (
              <div className="inline-flex items-center gap-3 px-5 py-4 rounded-2xl bg-white/10 border border-white/10 backdrop-blur-md">
                <Truck size={20} className="text-blue-300" />

                <div>
                  <p className="text-2xl font-bold">
                    {pendingCount}
                  </p>

                  <p className="text-xs text-slate-400">
                    Awaiting delivery
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats */}
          {!loading && (
            <div className="grid md:grid-cols-3 gap-5 mb-8">
              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <PackageCheck size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Total Orders
                </p>

                <p className="text-3xl font-bold">
                  {orders.length}
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5">
                  <Clock size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Pending Deliveries
                </p>

                <p className="text-3xl font-bold">
                  {pendingCount}
                </p>
              </div>

              <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
                  <CheckCircle2 size={21} />
                </div>

                <p className="text-sm text-slate-500 mb-2">
                  Delivered
                </p>

                <p className="text-3xl font-bold">
                  {deliveredCount}
                </p>
              </div>
            </div>
          )}

          {/* Orders Panel */}
          <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 md:p-7 border-b border-slate-200">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
                <div>
                  <h2 className="text-xl font-bold">
                    Delivery Orders
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    Manage all customer deliveries.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search
                      size={17}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) =>
                        setSearchTerm(e.target.value)
                      }
                      placeholder="Search customer or product..."
                      className="w-full sm:w-72 pl-11 pr-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </div>

                  {/* Status */}
                  <select
                    value={statusFilter}
                    onChange={(e) =>
                      setStatusFilter(e.target.value)
                    }
                    className="px-4 py-3 rounded-xl border border-slate-200 bg-white outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                  >
                    <option value="all">
                      All Orders
                    </option>
                    <option value="pending">
                      Pending
                    </option>
                    <option value="delivered">
                      Delivered
                    </option>
                  </select>
                </div>
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="p-6 md:p-8 space-y-5">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    key={item}
                    className="h-36 rounded-2xl bg-slate-100 animate-pulse"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filteredOrders.length === 0 && (
              <div className="py-20 px-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mx-auto mb-6">
                  <Truck size={28} />
                </div>

                <h3 className="text-2xl font-bold mb-3">
                  No orders found
                </h3>

                <p className="text-slate-500 max-w-md mx-auto">
                  There are no delivery orders matching your current search
                  or filter.
                </p>
              </div>
            )}

            {/* Order List */}
            {!loading && filteredOrders.length > 0 && (
              <div className="divide-y divide-slate-100">
                {filteredOrders.map((order) => {
                  const quantity = Number(order.quantity || 1);
                  const unitPrice = Number(order.price || 0);
                  const orderTotal = quantity * unitPrice;

                  return (
                    <article
                      key={order.id}
                      className="p-6 md:p-8 hover:bg-slate-50/60 transition"
                    >
                      <div className="grid lg:grid-cols-[1fr_auto] gap-7 items-center">
                        <div className="flex gap-5">
                          {/* Icon */}
                          <div
                            className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                              order.delivered
                                ? "bg-emerald-50 text-emerald-600"
                                : "bg-blue-50 text-blue-600"
                            }`}
                          >
                            {order.delivered ? (
                              <CheckCircle2 size={23} />
                            ) : (
                              <Truck size={23} />
                            )}
                          </div>

                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3 mb-3">
                              <h3 className="text-xl font-bold">
                                {order.product_name}
                              </h3>

                              <span
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                                  order.delivered
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-amber-50 text-amber-700"
                                }`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    order.delivered
                                      ? "bg-emerald-500"
                                      : "bg-amber-500"
                                  }`}
                                />

                                {order.delivered
                                  ? "Delivered"
                                  : "Pending"}
                              </span>
                            </div>

                            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
                              <div>
                                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                                  <User size={13} />
                                  Customer
                                </div>

                                <p className="font-semibold text-sm">
                                  {order.user_name || "Customer"}
                                </p>
                              </div>

                              <div>
                                <p className="text-xs text-slate-400 mb-1">
                                  Quantity
                                </p>

                                <p className="font-semibold text-sm">
                                  {quantity}
                                </p>
                              </div>

                              <div>
                                <p className="text-xs text-slate-400 mb-1">
                                  Order Total
                                </p>

                                <p className="font-semibold text-sm text-blue-600">
                                  KES {formatPrice(orderTotal)}
                                </p>
                              </div>

                              <div>
                                <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                                  <CalendarDays size={13} />
                                  Ordered
                                </div>

                                <p className="font-semibold text-sm">
                                  {formatDate(order.created_at)}
                                </p>
                              </div>
                            </div>

                            {order.address && (
                              <div className="flex items-start gap-2 mt-5 text-sm text-slate-500">
                                <MapPin
                                  size={16}
                                  className="text-blue-600 flex-shrink-0 mt-0.5"
                                />

                                <span>{order.address}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Action */}
                        <div className="lg:min-w-[190px]">
                          {order.delivered ? (
                            <div className="flex items-center justify-center gap-2 py-3 px-5 rounded-xl bg-emerald-50 text-emerald-700 font-semibold text-sm">
                              <CheckCircle2 size={17} />
                              Delivery Complete
                            </div>
                          ) : (
                            <button
                              onClick={() =>
                                markAsDelivered(order)
                              }
                              disabled={
                                updatingOrderId === order.id
                              }
                              className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-600/20 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              {updatingOrderId === order.id ? (
                                <>
                                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                  Updating...
                                </>
                              ) : (
                                <>
                                  <PackageCheck size={17} />
                                  Mark Delivered
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          {!loading && orders.length > 0 && (
            <p className="text-xs text-slate-400 text-center mt-6">
              Total value of orders shown in this dashboard: KES{" "}
              {formatPrice(totalOrderValue)}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default DeliveryOrders;